import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import {
	$addCards,
	$cards,
	$cursorCards,
	$hasMoreCards,
	$isFetchingCards,
	$setCards,
	$setCursorCards,
	$setHasMoreCards,
	$setIsFetchingCards,
	type ICardListItem,
} from '@/entities/card.entity';
import {
	$cardsFetchKey,
	$cardsListScrollTop,
	$cardsSearchField,
	$cardsSortBy,
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$setCardsFetchKey,
	$setCardsListScrollTop,
	onlineStatusToIsOnline,
	toServerCardsFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import CardListItem from '../cards/card-list-item';
import { LogoLoader } from '@/components/logo-loader';

const LIMIT = 35;

export default function CardsList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [cards, addCards, setCards] = useUnit([$cards, $addCards, $setCards]);
	const [hasMore, setHasMore] = useUnit([$hasMoreCards, $setHasMoreCards]);
	const [cursor, setCursor] = useUnit([$cursorCards, $setCursorCards]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingCards, $setIsFetchingCards]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$cardsSearchField,
		$cardsSortBy,
		$clientsSortDir,
		$cardsListScrollTop,
		$setCardsListScrollTop,
		$cardsFetchKey,
		$setCardsFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerCardsFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: cards.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 100,
		overscan: 5,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'cards',
		listRef,
		virtualizer,
		items: cards,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && cards.length > 0,
	});

	useEffect(() => {
		if (isDevMode) return;

		function addCardsHandler(data: { cards: ICardListItem[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.cards?.length) {
				addCards(data.cards);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addCards, addCardsHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addCards, addCardsHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setCards([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getCards, { ...queryPayload, cursor: null });
		}

		if (cards.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [currentFetchKey]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= cards.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getCards, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, cards.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !cards.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : cards.length === 0 ? (
				<div className='text-center mt-30 text-md'>Карты не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const card = cards[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<CardListItem card={card} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
